import { useState, useCallback } from "react";
import { roundRate } from "@/lib/utils";
import { Step } from "./constants";
import { CaretLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { DrawerHeader } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Currency } from "@/lib/types";

// Format rate value for display (up to 6 decimal places, no trailing zeros)
const formatRateValue = (rate: number): string => {
  if (!rate || !isFinite(rate)) return "";
  return parseFloat(rate.toFixed(6)).toString();
};

const ExchangeRateStep = ({
  setStep,
  fromCurrency,
  toCurrency,
  initialRate,
  onSave,
}: {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  fromCurrency?: Currency;
  toCurrency?: Currency;
  /** Current rate from useConversionRate — seeds the drawer's local input state. */
  initialRate: number | undefined;
  /** Called with the saved rate; routes through useConversionRate's setRate so the displayed value updates. */
  onSave: (rate: number | undefined) => void;
}) => {
  const { t } = useTranslation();

  // Dual-direction state:
  // fromToRate: 1 From Account Currency = X To Account Currency
  // toFromRate: 1 To Account Currency = X From Account Currency
  const [fromToRate, setFromToRate] = useState<string>(() =>
    initialRate !== undefined && initialRate > 0
      ? formatRateValue(initialRate)
      : ""
  );
  const [toFromRate, setToFromRate] = useState<string>(() =>
    initialRate !== undefined && initialRate > 0
      ? formatRateValue(1 / initialRate)
      : ""
  );

  const fromCode = fromCurrency?.code || "???";
  const toCode = toCurrency?.code || "???";

  // Handle "1 From = X To" input change (fromTo direction)
  const handleFromToChange = useCallback((value: string) => {
    setFromToRate(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      const inverse = 1 / numValue;
      setToFromRate(formatRateValue(inverse));
    } else if (value === "") {
      setToFromRate("");
    }
  }, []);

  // Handle "1 To = X From" input change (toFrom direction)
  const handleToFromChange = useCallback((value: string) => {
    setToFromRate(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      const inverse = 1 / numValue;
      setFromToRate(formatRateValue(inverse));
    } else if (value === "") {
      setFromToRate("");
    }
  }, []);

  const handleSave = () => {
    const numValue = parseFloat(fromToRate);
    if (!isNaN(numValue) && numValue > 0) {
      onSave(roundRate(numValue));
    }
    setStep(Step.INITAL);
  };

  return (
    <div>
      <DrawerHeader
        title={t("label.add_exchange_rate")}
        leftButton={
          <button type="button" onClick={() => setStep(Step.INITAL)}>
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />

      <p className="mx-auto mb-4 max-w-[280px] text-center text-sm font-medium leading-[18px] text-[#98B2D3]">
        {t("description.exchange_rate_hint")}
      </p>

      {/* 1 To Currency = X From Currency (more intuitive for users) */}
      <div className="mb-3 flex items-center gap-2">
        <span className="min-w-[70px] text-sm text-white/70">1 {toCode} =</span>
        <Input
          type="number"
          data-testid="transfer-form-exchange-rate-to-from-input"
          placeholder="0.00"
          value={toFromRate}
          onChange={(e) => handleToFromChange(e.target.value)}
          className="flex-1"
        />
        <span className="min-w-[40px] text-sm text-white/70">{fromCode}</span>
      </div>

      {/* 1 From Currency = X To Currency */}
      <div className="mb-4 flex items-center gap-2">
        <span className="min-w-[70px] text-sm text-white/70">
          1 {fromCode} =
        </span>
        <Input
          type="number"
          data-testid="transfer-form-exchange-rate-from-to-input"
          placeholder="0.00"
          value={fromToRate}
          onChange={(e) => handleFromToChange(e.target.value)}
          className="flex-1"
        />
        <span className="min-w-[40px] text-sm text-white/70">{toCode}</span>
      </div>

      <Button
        data-testid="transfer-form-exchange-rate-submit-button"
        className="mt-4 w-full"
        onClick={handleSave}
      >
        {t("button.add")}
      </Button>
    </div>
  );
};

export default ExchangeRateStep;
